import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { TYPE_COLORS } from "@/lib/types";
import { Bookmark, ArrowRight, MoveRight } from "lucide-react";
import { toast } from "sonner";

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export function Highlight({ text, terms }) {
  if (!terms?.length || !text) return text || null;
  const re = new RegExp(`(?<![a-z0-9])(${terms.map(escapeRe).join("|")})(?![a-z0-9])`, "gi");
  return text.split(re).map((part, i) =>
    terms.some((t) => t.toLowerCase() === part.toLowerCase()) ? (
      <mark key={`${i}-${part}`} className="rounded-sm bg-gold/30 px-0.5 text-foreground">{part}</mark>
    ) : (
      <span key={`${i}-${part}`}>{part}</span>
    )
  );
}

export default function QuestionCard({ q, index = 0, terms = [], linkState }) {
  const { isFavorite, toggleFavorite } = useAuth();
  const navigate = useNavigate();
  const [pulse, setPulse] = useState(false);
  const saved = isFavorite(q.id);

  const onType = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/type/${encodeURIComponent(q.type)}`);
  };

  const onSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPulse(true);
    setTimeout(() => setPulse(false), 450);
    const res = await toggleFavorite(q.id);
    if (res?.needAuth) toast.info("Sign in to save questions to your study.");
    else if (res?.added) toast.success("Saved to your study.");
  };

  const typeClass = TYPE_COLORS[q.type] || "bg-muted text-muted-foreground";

  return (
    <Link
      to={terms?.length ? `/question/${q.id}?hl=${encodeURIComponent(terms.join(" "))}` : `/question/${q.id}`}
      state={linkState}
      data-testid={`question-card-${q.id}`}
      style={{ animationDelay: `${Math.min(index, 8) * 60}ms` }}
      className="group relative flex h-full flex-col justify-between overflow-hidden rounded-xl border border-border bg-card p-6 transition-colors duration-300 hover:border-primary/40 hover:shadow-xl"
    >
      <div>
        <div className="mb-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span
              className={`rounded-md px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider ${
                q.testament === "OT"
                  ? "bg-primary/12 text-primary"
                  : "bg-secondary/15 text-secondary dark:text-sky-300"
              }`}
            >
              {q.testament === "OT" ? "Old Testament" : "New Testament"}
            </span>
            <span className="no-reftag font-mono text-xs font-medium text-muted-foreground">{q.reference}</span>
            {q.match && q.match !== "question" && q.match !== "reference" && (
              <span className="rounded-full border border-border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground" data-testid={`card-match-${q.id}`}>
                found in {q.match}
              </span>
            )}
          </div>
          <button
            data-testid={`save-favorite-btn-${q.id}`}
            onClick={onSave}
            aria-label={saved ? "Unsave" : "Save"}
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors ${
              saved ? "text-accent" : "text-muted-foreground hover:text-accent"
            }`}
          >
            <Bookmark className={`h-[18px] w-[18px] ${saved ? "fill-current" : ""} ${pulse ? "qg-star-pulse" : ""}`} />
          </button>
        </div>

        <p className="font-serif text-xl font-medium leading-snug text-foreground sm:text-2xl">
          <span className="mr-0.5 text-2xl text-gold">&ldquo;</span>
          <Highlight text={q.question_kjv} terms={terms} />
          <span className="ml-0.5 text-2xl text-gold">&rdquo;</span>
        </p>
        {q.snippet && q.match && q.match !== "question" && (
          <p className="mt-3 border-l-2 border-gold/50 pl-3 text-sm leading-relaxed text-muted-foreground" data-testid={`card-snippet-${q.id}`}>
            <Highlight text={q.snippet} terms={terms} />
          </p>
        )}
      </div>

      <div className="mt-5 flex items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            <span className="font-semibold text-foreground/80">{q.who_asked.join(", ")}</span>
            <MoveRight className="h-3 w-3" />
            <span>{q.asked_to.join(", ")}</span>
          </div>
          {q.type && <button
            type="button"
            onClick={onType}
            data-testid={`card-type-${q.id}`}
            title={`All ${q.type} questions`}
            className={`mt-2 inline-block rounded-full px-2.5 py-0.5 text-[11px] font-semibold transition-transform hover:scale-105 hover:underline ${typeClass}`}
          >
            {q.type}
          </button>}
        </div>
        <ArrowRight className="h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200 group-hover:translate-x-1 group-hover:text-primary" />
      </div>
    </Link>
  );
}
